import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/shared/auth/AuthContext';
import { Activity, Building2, ScrollText, LogOut, ShieldCheck, Menu, X , UsersRound } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Separator } from '@/shared/ui/separator';
import { useState } from 'react';

/**
 * Barre latérale de la console de plateforme.
 *
 * Volontairement séparée de celle du CRM. Deux raisons : le super admin n'a rien
 * à faire dans le menu métier, et la barre du CRM interroge `/alerts/count`
 * toutes les minutes — un appel sur lequel il ne récolterait que des 403.
 */
const ENTREES = [
  { to: '/superadmin', label: 'Sous-comptes', icon: Building2, end: true },
  { to: '/superadmin/comptes', label: 'Comptes', icon: UsersRound },
  { to: '/superadmin/journal', label: 'Journal', icon: ScrollText },
  { to: '/telemetrie', label: 'Telemetrie', icon: Activity },
];

export default function ConsoleSidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [ouvert, setOuvert] = useState(false);

  const deconnexion = () => { logout(); navigate('/login'); };

  const contenu = (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 px-5 py-5">
        <ShieldCheck className="h-5 w-5 text-emerald-400" />
        <div>
          <p className="text-sm font-semibold text-white">FlexCore</p>
          <p className="text-[10px] uppercase tracking-wider text-slate-400">Console plateforme</p>
        </div>
      </div>
      <Separator className="bg-slate-700" />

      <nav className="flex-1 space-y-1 p-3">
        {ENTREES.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={() => setOuvert(false)}
            data-testid={`console-nav-${to.split('/').pop()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition ${
                isActive ? 'bg-slate-700 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </NavLink>
        ))}
      </nav>

      <Separator className="bg-slate-700" />
      <div className="p-3">
        <div className="px-2 py-2">
          <p className="truncate text-sm text-white" data-testid="console-user-name">{user?.name}</p>
          <p className="text-[11px] text-slate-400">Super admin</p>
        </div>
        <Button
          variant="ghost"
          onClick={deconnexion}
          data-testid="console-logout"
          className="w-full justify-start gap-3 text-slate-300 hover:bg-slate-800 hover:text-white"
        >
          <LogOut className="h-4 w-4" />
          Déconnexion
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <button
        onClick={() => setOuvert(!ouvert)}
        data-testid="console-burger"
        className="fixed left-3 top-3 z-50 rounded-lg bg-slate-900 p-2 text-white md:hidden"
        aria-label="Menu"
      >
        {ouvert ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      <aside className="hidden w-64 shrink-0 bg-slate-900 md:block">{contenu}</aside>

      {ouvert && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOuvert(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 bg-slate-900">{contenu}</aside>
        </div>
      )}
    </>
  );
}
